import { useEffect, useRef, useState } from "react";

import {
  useGetTaskLiveViewQuery,
  useReleaseTaskLiveViewMutation,
} from "features/adminUsers/adminUsers";
import { botsManagementWs, BotsManagementWsEvent } from "features/ws/botsManagementWs";

import CaptchaLiveViewDialog from "./CaptchaLiveViewDialog";

type Props = {
  taskId: number | null;
  canEdit: boolean;
  onClose: () => void;
};

/**
 * Loads the live-view session for one task and shows the operator's window into
 * its browser. While the WebSocket delivers, its live_view/task events drive the
 * refetch; the slow poll only covers a socket that is not delivering.
 *
 * Nothing to show means no session is being served - the bot is still bringing
 * the view up, or it never got a slot.
 */
export default function TaskLiveViewDialog({
  taskId,
  canEdit,
  onClose,
}: Props) {
  const [wsConnected, setWsConnected] = useState(() => botsManagementWs.isConnected());
  const { data: liveView, refetch } = useGetTaskLiveViewQuery(taskId as number, {
    skip: taskId == null,
    pollingInterval: taskId == null || wsConnected ? 0 : 5000,
  });
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (taskId == null) return undefined;
    const unStatus = botsManagementWs.subscribeStatus(setWsConnected);
    const unEvents = botsManagementWs.subscribe((event: BotsManagementWsEvent) => {
      if (event.task?.task_id !== taskId) return;
      if (refetchTimerRef.current) return;
      refetchTimerRef.current = setTimeout(() => {
        refetchTimerRef.current = null;
        void refetch();
      }, 400);
    });
    return () => {
      unStatus();
      unEvents();
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, [taskId, refetch]);

  const [releaseLiveView, { isLoading: releasing }] =
    useReleaseTaskLiveViewMutation();

  return (
    <CaptchaLiveViewDialog
      open={taskId != null}
      liveView={liveView ?? null}
      taskLabel={taskId != null ? `Task ${taskId}` : ""}
      canControl={canEdit}
      releasing={releasing}
      onRelease={() => {
        if (taskId != null) releaseLiveView(taskId);
      }}
      onClose={onClose}
    />
  );
}
